/**
 * Quick task 261002-dfh — the "Triager proposal" control of the task detail
 * slide-over.
 *
 * Rendered by TaskDetailSlideOver only when the task carries `robot-candidate`
 * and is unassigned (a Factory™ Triager proposal awaiting a human), for an
 * internal admin / project PM. Kept as its own component so the mutation hook
 * mounts only then. Decline swaps `robot-candidate` for `no-robot` server-side
 * (clearing the label alone would put the task back in the candidate pool);
 * Accept assigns the Robot.
 *
 * MKS-278: a task a human ALREADY approved while an MKS-241 overlap hold was on
 * matches that same condition — unassigned, still labelled — so the section used
 * to offer Accept on a decided proposal. It now reads the approval state
 * (`GET /api/tasks/:taskId/triager-proposal`) and, when the task is approved,
 * renders who approved it and what it waits for instead of Accept. Decline stays
 * available as "Withdraw approval" (it clears the marker and marks the task
 * no-robot, exactly as it does today) and an admin can force the dispatch
 * through the existing hold override.
 */
import { Button } from '../common/Button';
import { HeldByText } from '../common/HeldByText';
import { useAuth } from '../../services/authContext';
import { formatDisplayDateTime } from '../../utils/format';
import {
  useDispatchHeldTaskAnyway,
  useTriagerProposalDecision,
  useTriagerProposalState,
} from '../../hooks/useTriagerProposal';

export interface TriagerProposalSectionProps {
  taskId: number;
  projectId: number;
  onError?: (message: string) => void;
}

const SECTION_CLASS = 'pt-2 border-t border-ms-border-subtle';
const LABEL_CLASS = 'text-xs font-medium text-ms-text-secondary mb-1';

export function TriagerProposalSection({ taskId, projectId, onError }: TriagerProposalSectionProps) {
  const { isAdmin } = useAuth();
  const decision = useTriagerProposalDecision();
  const state = useTriagerProposalState(taskId);
  const dispatchAnyway = useDispatchHeldTaskAnyway(taskId, String(projectId));

  function decide(action: 'accept' | 'decline') {
    decision.mutate(
      { taskId, projectId: String(projectId), action },
      {
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }

  // The state is not in yet: show the section without any decision button.
  // Offering Accept here is exactly the bug — it would be offered on an approved
  // task for as long as the read takes.
  if (state.isPending) {
    return (
      <div className={SECTION_CLASS} data-testid="task-detail-triager-proposal">
        <div className={LABEL_CLASS}>Triager proposal</div>
        <p className="text-xs text-ms-text-muted">Checking the proposal state…</p>
      </div>
    );
  }

  // A failed read falls through to the two buttons rather than locking the section: an open
  // proposal must stay decidable. Clicking Accept on a task that turns out to be approved
  // already is a no-op server-side (MKS-278), so the worst case is a wasted click.
  const approvedAt = state.data?.robotHoldApprovedAt ?? null;

  if (approvedAt !== null) {
    const approver = state.data?.approvedBy?.name;
    const heldBy = state.data?.heldBy ?? [];
    return (
      <div className={SECTION_CLASS} data-testid="task-detail-triager-proposal">
        <div className={LABEL_CLASS}>Triager proposal</div>
        <p className="text-xs text-ms-text-muted mb-2" data-testid="triager-proposal-approved">
          {approver ? `Approved by ${approver}` : 'Approved'} · {formatDisplayDateTime(approvedAt)}
          {heldBy.length > 0 ? (
            <>
              {' — '}
              <HeldByText heldBy={heldBy} />. The Robot picks it up when that is done.
            </>
          ) : (
            ' — nothing holds it any more; the Robot picks it up within 5 minutes.'
          )}
        </p>
        {dispatchAnyway.isError && (
          <p className="text-xs text-red-600 mb-2">{dispatchAnyway.error.message}</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            isLoading={decision.isPending}
            disabled={decision.isPending || dispatchAnyway.isPending}
            onClick={() => decide('decline')}
          >
            Withdraw approval
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              variant="ghost"
              isLoading={dispatchAnyway.isPending}
              disabled={decision.isPending || dispatchAnyway.isPending}
              onClick={() => dispatchAnyway.mutate()}
            >
              Dispatch anyway
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={SECTION_CLASS} data-testid="task-detail-triager-proposal">
      <div className={LABEL_CLASS}>Triager proposal</div>
      <p className="text-xs text-ms-text-muted mb-2">
        The triager proposed handing this task to the Robot. Accept assigns it; Decline marks it
        no-robot so the triager skips it.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          isLoading={decision.isPending && decision.variables?.action === 'accept'}
          disabled={decision.isPending}
          onClick={() => decide('accept')}
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="secondary"
          isLoading={decision.isPending && decision.variables?.action === 'decline'}
          disabled={decision.isPending}
          onClick={() => decide('decline')}
        >
          Decline
        </Button>
      </div>
    </div>
  );
}
